import {
  ActionIcon,
  Box,
  Group,
  Loader,
  Menu,
  Paper,
  Text,
} from '@mantine/core';
import { IconDots, IconEdit, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useDateDisplay } from '@hooks/useDateDisplay';
import { getSurgeryCodeText, SurgeryInputItem } from '@/utils/surgery.utils';
import { LabeledField } from '@/components/LabeledField';

export type ProcedureAction = 'edit' | 'remove';

export interface ProcedureListItemProps {
  procedure: SurgeryInputItem;
  onAction?: (action: ProcedureAction) => void;
  isDeleting?: boolean;
  hideActions?: boolean;
}

export const ProcedureListItem = ({
  procedure,
  onAction,
  isDeleting = false,
  hideActions = false,
}: ProcedureListItemProps) => {
  const { t } = useTranslation(['patient-pages', 'common']);
  const formatDate = useDateDisplay();

  // Use utility functions for safe nested access
  const procedureName =
    getSurgeryCodeText(procedure) ||
    t('surgeries.unknownProcedure', {
      ns: 'patient-pages',
      defaultValue: 'Unknown Procedure',
    });

  // Extract individual fields
  const performedDate = procedure.performedDate;
  const notes = procedure.notes;

  // Determine which actions are available
  const canEdit = !isDeleting;
  const canRemove = !isDeleting;
  const hasActions = !hideActions && (canEdit || canRemove);

  return (
    <Paper p="sm" withBorder radius="md">
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Box flex={1}>
          {/* Procedure name */}
          <Text fw={600} size="sm" mb={8}>
            {procedureName}
          </Text>

          {/* Fields */}
          <LabeledField
            label={t('surgeries.performedDate', {
              ns: 'patient-pages',
              defaultValue: 'Date Performed',
            })}
            value={formatDate(performedDate)}
          />

          {/* Notes */}
          {notes && (
            <Box mt={4}>
              <LabeledField
                label={t('surgeries.notes', {
                  ns: 'patient-pages',
                  defaultValue: 'Notes',
                })}
                value={notes}
              />
            </Box>
          )}
        </Box>

        {/* Actions menu or loading indicator */}
        {isDeleting ? (
          <Loader size="sm" />
        ) : (
          hasActions && (
            <Menu shadow="md" width={160} position="bottom-end">
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" size="sm">
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                {canEdit && (
                  <Menu.Item
                    leftSection={<IconEdit size={14} />}
                    onClick={() => onAction?.('edit')}
                  >
                    {t('edit', {
                      ns: 'common',
                      defaultValue: 'Edit',
                    })}
                  </Menu.Item>
                )}
                {canRemove && (
                  <Menu.Item
                    leftSection={<IconTrash size={14} />}
                    onClick={() => onAction?.('remove')}
                    color="red"
                  >
                    {t('delete', {
                      ns: 'common',
                      defaultValue: 'Delete',
                    })}
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )
        )}
      </Group>
    </Paper>
  );
};
